import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    getDistrictList,
    getBlockList,
    getGpList,
    getISGPPWiseReceipt,
} from "../../Service/TotalAvailableFund";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ISGPReceipt = () => {
    const getCurrentDate = () => new Date().toISOString().split("T")[0];

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState(getCurrentDate());

    const [isgpReceiptDetails, setIsgpReceiptDetails] = useState([]);
    const [district, setDistrict] = useState("");
    const [block, setBlock] = useState("");
    const [gp, setGp] = useState("");
    const [getDistrictDataList, setDistrictDataList] = useState([]);
    const [getBlockDataList, setBlockDataList] = useState([]);
    const [getGpDataList, setGpDataList] = useState([]);
    const [reportFor, setReportFor] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getDistrictList().then(function (result) {
            const response = result?.data;
            setDistrictDataList(response || []);
        });
    }, []);

    let DistrictListDropDown = <option>Loading...</option>;
    if (getDistrictDataList && getDistrictDataList.length > 0) {
        DistrictListDropDown = getDistrictDataList.map((distRow, index) => (
            <option key={index} value={distRow.DIST_LGD}>
                {distRow.DIST_NAME}
            </option>
        ));
    }

    const onDistrict = (e) => {
        const selectedDistrict = e.target.value;

        setDistrict(selectedDistrict);
        setBlock("");
        setGp("");
        setBlockDataList([]);
        setGpDataList([]);
        setIsgpReceiptDetails([]);

        if (selectedDistrict) {
            getBlockList(selectedDistrict).then(function (result) {
                const response = result?.data;
                setBlockDataList(response || []);
            });
        }
    };

    let BlockListDropDown = <option>Loading...</option>;
    if (getBlockDataList && getBlockDataList.length > 0) {
        BlockListDropDown = getBlockDataList.map((blRow, index) => (
            <option key={index} value={blRow.BLOCK_LGD}>
                {blRow.BLOCK_NAME}
            </option>
        ));
    }

    const onBlock = (e) => {
        const selectedBlock = e.target.value;

        setBlock(selectedBlock);
        setGp("");
        setGpDataList([]);
        setIsgpReceiptDetails([]);

        if (selectedBlock) {
            getGpList(selectedBlock).then(function (result) {
                const response = result?.data;
                setGpDataList(response || []);
            });
        }
    };

    let GpListDropDown = <option>Loading...</option>;
    if (getGpDataList && getGpDataList.length > 0) {
        GpListDropDown = getGpDataList.map((gpRow, index) => (
            <option key={index} value={gpRow.GP_LGD}>
                {gpRow.GP_NAME}
            </option>
        ));
    }

    const onGp = (e) => {
        setGp(e.target.value);
        setIsgpReceiptDetails([]);
    };

    const onReportFor = (e) => {
        setReportFor(e.target.value);
        setIsgpReceiptDetails([]);
    };

    const getRows = () => {
        if (Array.isArray(isgpReceiptDetails)) {
            return isgpReceiptDetails;
        }

        if (Array.isArray(isgpReceiptDetails?.dtls)) {
            return isgpReceiptDetails.dtls;
        }

        if (Array.isArray(isgpReceiptDetails?.data)) {
            return isgpReceiptDetails.data;
        }

        if (Array.isArray(isgpReceiptDetails?.result)) {
            return isgpReceiptDetails.result;
        }

        return [];
    };

    const rows = getRows();

    const fixedColumns = ["LGD_CODE", "PRI"];

    const dynamicColumns = Array.from(
        new Set(
            rows.flatMap((row) =>
                Object.keys(row || {}).filter((key) => !fixedColumns.includes(key))
            )
        )
    );

    const cleanHeader = (text) => {
        return String(text || "")
            .replace(/\u00A0/g, " ")
            .replace(/\s+/g, " ")
            .trim();
    };

    const getNumericValue = (value) => {
        if (value === undefined || value === null || value === "") {
            return 0;
        }

        const numberValue = Number(String(value).replace(/,/g, ""));
        return isNaN(numberValue) ? 0 : numberValue;
    };

    const formatAmount = (amount) => {
        return getNumericValue(amount).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const getColumnTotal = (columnName) => {
        return rows.reduce((sum, item) => {
            return sum + getNumericValue(item?.[columnName]);
        }, 0);
    };

    const getReportForName = () => {
        if (reportFor === "1") return "Zilla Parishad";
        if (reportFor === "2") return "Panchayat Samiti";
        if (reportFor === "3") return "Gram Panchayat";
        return "All";
    };

    const onSearch = () => {
        if (!reportFor) {
            toast.error("Please select Report For option");
            return;
        }

        if (!fromDate) {
            toast.error("Please select From Date");
            return;
        }

        if (!toDate) {
            toast.error("Please select To Date");
            return;
        }

        if (fromDate && toDate && fromDate > toDate) {
            toast.error("From Date cannot be greater than To Date");
            return;
        }

        setLoading(true);
        setIsgpReceiptDetails([]);

        getISGPPWiseReceipt(
            district || 0,
            block || 0,
            gp || 0,
            reportFor || 0,
            fromDate || "",
            toDate || ""
        )
            .then((response) => {
                if (response.status === 200) {
                    const responseData = response?.data;

                    if (
                        responseData?.statusMsg &&
                        responseData?.statusMsg !== "Success"
                    ) {
                        toast.error(responseData.statusMsg);
                    } else {
                        toast.success("Data Fetched Successfully");
                    }

                    setIsgpReceiptDetails(responseData || []);
                } else {
                    toast.error("Failed to fetch data");
                }
            })
            .catch((error) => {
                console.error(error);
                toast.error("Failed to fetch data");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const splitPriForExcel = (priValue) => {
        const priText = String(priValue || "")
            .replace(/\u00A0/g, " ")
            .replace(/\s+/g, " ")
            .trim();

        if (priText.includes("::")) {
            const parts = priText.split("::");

            return {
                hasBlockGp: true,
                pri: "",
                block: parts[0]?.trim() || "",
                gp: parts.slice(1).join("::")?.trim() || "",
            };
        }

        return {
            hasBlockGp: false,
            pri: priText,
            block: "",
            gp: "",
        };
    };

    const exportToExcel = () => {
        const excelRows = rows.map((item) => {
            return {
                item,
                splitData: splitPriForExcel(item?.PRI),
            };
        });

        const hasBlockGpData = excelRows.some((row) => row.splitData.hasBlockGp);
        const hasPriData = excelRows.some(
            (row) => !row.splitData.hasBlockGp && row.splitData.pri
        );

        const worksheetData = excelRows.map((row, index) => {
            const item = row.item;
            const splitData = row.splitData;

            const rowData = {
                "SL No.": index + 1,
                "LGD Code": item?.LGD_CODE || "",
            };

            if (hasPriData) {
                rowData.PRI = splitData.hasBlockGp ? "" : splitData.pri;
            }

            if (hasBlockGpData) {
                rowData.Block = splitData.hasBlockGp ? splitData.block : "";
                rowData.GP = splitData.hasBlockGp ? splitData.gp : "";
            }

            dynamicColumns.forEach((column) => {
                rowData[cleanHeader(column)] = getNumericValue(item?.[column]);
            });

            return rowData;
        });

        const grandTotalRow = {
            "SL No.": "",
            "LGD Code": "",
        };

        if (hasPriData) {
            grandTotalRow.PRI = "Grand Total";
        }

        if (hasBlockGpData) {
            grandTotalRow.Block = hasPriData ? "" : "Grand Total";
            grandTotalRow.GP = "";
        }

        dynamicColumns.forEach((column) => {
            grandTotalRow[cleanHeader(column)] = getColumnTotal(column);
        });

        const finalWorksheetData = [...worksheetData, grandTotalRow];

        const worksheet = XLSX.utils.json_to_sheet(finalWorksheetData);

        const columnWidths = [
            { wch: 8 },
            { wch: 15 },
        ];

        if (hasPriData) {
            columnWidths.push({ wch: 40 });
        }

        if (hasBlockGpData) {
            columnWidths.push({ wch: 25 });
            columnWidths.push({ wch: 30 });
        }

        worksheet["!cols"] = [
            ...columnWidths,
            ...dynamicColumns.map((column) => ({
                wch: column === "TOTAL" ? 18 : 35,
            })),
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "ISGP Receipt");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, "ISGP_Receipt_Report.xlsx");
    };

    return (
        <div>
            <ToastContainer />

            <div
                className="bg-white rounded-lg p-2 flex flex-col flex-grow"
                style={{ marginTop: "-40px" }}
            >
                <legend className="text-lg font-semibold text-cyan-700">
                    Provision wise OSR Collection As on {new Date().toLocaleString()}
                </legend>

                <div className="flex flex-col space-y-2 py-1">
                    <div className="flex flex-col w-full mb-4 space-y-2">
                        <div className="flex items-center flex-wrap gap-y-2">
                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    District
                                </label>
                                <select
                                    autoComplete="off"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    value={district}
                                    onChange={onDistrict}
                                >
                                    <option value="">Select District</option>
                                    {DistrictListDropDown}
                                </select>
                            </div>

                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Block
                                </label>
                                <select
                                    autoComplete="off"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    onChange={onBlock}
                                    value={block}
                                >
                                    <option value="">Select Block</option>
                                    {BlockListDropDown}
                                </select>
                            </div>

                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Gram Panchayat
                                </label>
                                <select
                                    autoComplete="off"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    onChange={onGp}
                                    value={gp}
                                >
                                    <option value="">Select Gram Panchayat</option>
                                    {GpListDropDown}
                                </select>
                            </div>

                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Report For <span className="text-red-500">*</span>
                                </label>
                                <select
                                    autoComplete="off"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    onChange={onReportFor}
                                    value={reportFor}
                                >
                                    <option value="">Select Report For</option>
                                    <option value="4">ALL</option>
                                    <option value="1">ZP</option>
                                    <option value="2">PS</option>
                                    <option value="3">GP</option>
                                </select>
                            </div>

                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    From Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    max={toDate || getCurrentDate()}
                                />
                            </div>

                            <div className="w-1/4 px-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    To Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="text-sm block w-full p-1 h-9 border border-gray-300 rounded-md"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    min={fromDate || ""}
                                    max={getCurrentDate()}
                                />
                            </div>

                            <div className="w-1/6 px-2">
                                <button
                                    type="button"
                                    className="btn-submit h-9 px-4 mt-5 shadow-sm text-white hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    onClick={onSearch}
                                    disabled={loading}
                                >
                                    {loading ? "Loading..." : "Search"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div
                            className="flex items-center justify-center bg-gray-200"
                            style={{ marginTop: "10px", minHeight: "80px" }}
                        >
                            <h1 className="text-xl font-semibold text-black-800">
                                Loading...
                            </h1>
                        </div>
                    ) : !rows || rows.length === 0 ? (
                        <div
                            className="flex items-center justify-center bg-gray-200"
                            style={{ marginTop: "10px" }}
                        >
                            <div className="text-center">
                                <h1 className="text-xl font-semibold text-black-800">
                                    No Data Found
                                </h1>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto p-4">
                            <div className="flex justify-between items-center mb-2">
                                <button
                                    onClick={exportToExcel}
                                    className="bg-green-600 text-white px-2 py-2 rounded hover:bg-green-700 transition"
                                >
                                    Download Excel
                                </button>

                                <h2 className="text-lg font-semibold text-gray-800 text-center animate-blink flex-1">
                                    {getReportForName()} Provision wise OSR Collection
                                </h2>
                            </div>

                            <div className="border rounded overflow-hidden">
                                <div className="max-h-[500px] overflow-auto relative">
                                    <table className="min-w-full divide-y divide-gray-200 border text-sm">
                                        <thead className="bg-cyan-600 text-white sticky top-0 z-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left bg-cyan-600 z-50 whitespace-nowrap">
                                                    SL No.
                                                </th>

                                                <th className="px-4 py-2 text-left bg-cyan-600 whitespace-nowrap">
                                                    LGD Code
                                                </th>

                                                <th className="px-4 py-2 text-left bg-cyan-600 min-w-[260px]">
                                                    PRI
                                                </th>

                                                {dynamicColumns.map((column, index) => (
                                                    <th
                                                        key={index}
                                                        className={`px-4 py-2 text-right bg-cyan-600 whitespace-normal ${column === "TOTAL"
                                                            ? "min-w-[160px]"
                                                            : "min-w-[240px]"
                                                            }`}
                                                    >
                                                        {cleanHeader(column)}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {rows.map((item, rowIndex) => (
                                                <tr key={rowIndex} className="bg-white">
                                                    <td className="px-4 py-2 bg-white whitespace-nowrap">
                                                        {rowIndex + 1}
                                                    </td>

                                                    <td className="px-4 py-2 whitespace-nowrap">
                                                        {item?.LGD_CODE}
                                                    </td>

                                                    <td className="px-4 py-2 font-medium min-w-[260px]">
                                                        {item?.PRI}
                                                    </td>

                                                    {dynamicColumns.map((column, colIndex) => (
                                                        <td
                                                            key={colIndex}
                                                            className={`px-4 py-2 text-right ${column === "TOTAL"
                                                                ? "font-bold bg-gray-100"
                                                                : ""
                                                                }`}
                                                        >
                                                            {formatAmount(item?.[column])}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>

                                        <tfoot className="bg-gray-300 font-semibold text-black sticky bottom-0 z-40">
                                            <tr>
                                                <td className="px-4 py-2 bg-gray-300"></td>

                                                <td className="px-4 py-2 bg-gray-300"></td>

                                                <td className="px-4 py-2 text-left bg-gray-300">
                                                    Grand Total
                                                </td>

                                                {dynamicColumns.map((column, index) => (
                                                    <td
                                                        key={index}
                                                        className={`px-4 py-2 text-right ${column === "TOTAL"
                                                            ? "font-bold bg-gray-400"
                                                            : "bg-gray-300"
                                                            }`}
                                                    >
                                                        {formatAmount(getColumnTotal(column))}
                                                    </td>
                                                ))}
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ISGPReceipt;